import React, { useState, useRef, useEffect } from 'react';
import cx from 'classnames';
import debounce from 'lodash.debounce';

import { PaginationDots } from '@xingternal/pagination-dots';
import { Button } from '@xingternal/button';

import withPresets from '@xingternal/with-presets';
import { IconArrowLeft, IconArrowRight } from '@xingternal/icons';
import { mergeStyles } from '@xingternal/merge-styles';

import * as skins from './skins';

import base from './Carousel.css';

type CarouselProps = {
  /** define starting active card */
  activeCard: number;
  /** callback function for arrow click. Provides {direction, page, totalPages} */
  onPageChange: (params: OnEventProps) => void;
  //  /** callback function for arrow click. Provides {direction, page, totalPages} */
  onArrowClick: (params: OnEventProps) => void;
  /** children to be a list of cards */
  children: React.ReactNode;
  /** add your styles as skin={card: styles.yourHeroClassName} */
  skin: { [key: string]: string };
  className: string;
};

type OnEventProps = {
  direction: string;
  page: number;
  total: number;
};

const Carousel = ({
  activeCard,
  onArrowClick,
  onPageChange,
  children,
  skin,
  className,
  ...props
}: CarouselProps) => {
  const [firstRender, setFirstRender] = useState<any>(true);
  const [card, setCard] = useState<number>(activeCard);
  const [dimensions, setDimensions] = useState<any>({});
  const carouselRef = useRef<any>(null);
  const isResizingRef = useRef<any>(false);
  const cardRef = useRef<any>(false);
  const dimensionsRef = useRef<any>(false);
  const scrollingRef = useRef<any>(null);

  const styles = mergeStyles(base, skin);

  const getFirstCardInView = (card: number) => {
    const length = React.Children.count(children);
    const numOfCards = dimensionsRef.current.numOfCards;

    /* we should guarantee that the last card is not alone in the view */
    if (card + numOfCards > length) {
      return length - numOfCards > 0 ? length - numOfCards : 0;
    }
    return card > 0 ? card : 0;
  };

  const canSetCard = (card: number) => {
    const length = React.Children.count(children);

    const isValidStopPoint =
      card % dimensionsRef.current.numOfCards ===
        cardRef.current % dimensionsRef.current.numOfCards ||
      card === 0 ||
      card + dimensionsRef.current.numOfCards === length;

    return (
      isValidStopPoint &&
      !isResizingRef.current &&
      scrollingRef.current === null
    );
  };

  const handleScroll = debounce(() => {
    if (!carouselRef || !scrollingRef || !dimensionsRef) return;

    const currentScroll = carouselRef.current?.scrollLeft;
    const finalScroll = scrollingRef.current;
    const card = Math.round(currentScroll / dimensionsRef.current.cardWidth);

    if (finalScroll !== null && Math.abs(finalScroll - currentScroll) < 4) {
      scrollingRef.current = null;
    }

    canSetCard(card) && setCard(card);
  }, 50);

  const scroll = (card: number) => {
    if (!carouselRef.current) return;

    const { totalWidth, carouselViewWidth, cardWidth, length } =
      dimensionsRef.current;
    const firstCardInView = getFirstCardInView(card);

    const scrollToPosition =
      card === length - 1
        ? totalWidth - carouselViewWidth // we need this because in mobile we are displaying part of the previous card
        : firstCardInView * cardWidth;

    if (typeof carouselRef.current.scrollTo === 'function') {
      carouselRef.current.scrollTo({
        left: scrollToPosition,
        behavior: isResizingRef.current || firstRender ? 'auto' : 'smooth',
      });
    }
    scrollingRef.current = scrollToPosition;
    handleScroll();
  };

  const updateDimensions = () => {
    if (!carouselRef.current) return;

    const firstCard = carouselRef.current.firstChild;
    const cardsArray = Array.prototype.slice.call(carouselRef.current.children);
    const totalCardsWidth = cardsArray.reduce(
      (a, el) => a + el.getBoundingClientRect().width,
      0
    );
    const offsetWidth = totalCardsWidth / cardsArray.length; // we get an average because some cards can be 1px bigger than others
    const margin = parseInt(window?.getComputedStyle(firstCard).marginRight);
    const cardWidth = offsetWidth + margin;
    const carouselViewWidth =
      carouselRef.current.parentNode.getBoundingClientRect().width;
    const totalWidth = carouselRef.current.scrollWidth;

    const dimensions = {
      cardWidth,
      carouselViewWidth,
      totalWidth,
      length: React.Children.count(children),
      numOfCards: Math.round(carouselViewWidth / cardWidth),
    };

    // update dimensions
    setDimensions(dimensions);
    dimensionsRef.current = dimensions;
  };

  const handleResizeDebounce = (card: number) => {
    updateDimensions();

    // scroll to the original position
    scroll(card);

    isResizingRef.current = null;
  };

  const handleResize = () => {
    if (isResizingRef.current) return;
    isResizingRef.current = setTimeout(
      () => handleResizeDebounce(cardRef.current),
      500
    );
  };

  // we define if the card should be a stoping point or not
  const getChildClassname = (i: any) => {
    const currentCenter = card + Math.ceil(dimensions.numOfCards / 2) - 1;
    const positionInView = currentCenter - card;
    const isFirstCard = positionInView % dimensions.numOfCards === 0;
    const shouldStop =
      i % dimensions.numOfCards === currentCenter % dimensions.numOfCards;

    return shouldStop
      ? isFirstCard
        ? styles.cardSnapStopAlignStart
        : styles.cardSnapStopAlignCenter
      : null;
  };

  // sets initial active card
  useEffect(() => {
    if (isResizingRef.current) {
      scroll(card);
    }
    setFirstRender(false);
  }, [dimensions]);

  const updateDimensionsAndScroll = async () => {
    await updateDimensions();
    scroll(card);
  };

  useEffect(() => {
    updateDimensionsAndScroll();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    // triggers onPageChange()
    cardRef.current !== false &&
      onPageChange &&
      onPageChange({
        direction: cardRef.current > card ? 'left' : 'right',
        page: Math.ceil(card / dimensions.numOfCards) + 1,
        total: Math.ceil(dimensions.length / dimensions.numOfCards),
      });

    cardRef.current = card;
  }, [card]);

  const currentPage = Math.ceil(card / dimensions.numOfCards) + 1;
  const totalPages = Math.ceil(dimensions.length / dimensions.numOfCards);

  const showFirstButton = !!card;
  const showLastButton =
    card + dimensions.numOfCards < React.Children.count(children);

  return (
    <span className={cx({ [styles.firstRender]: firstRender && activeCard })}>
      <div className={cx(className, styles.carousel)} {...props}>
        <div className={styles.removeScrollBar}>
          <div
            className={styles.carouselMain}
            ref={carouselRef}
            onScroll={(e) => handleScroll(e)}
          >
            {React.Children.map(children, (child, i) => (
              <div className={getChildClassname(i)} key={i}>
                {child}
              </div>
            ))}
          </div>
        </div>
        {showFirstButton && (
          <Button
            data-qa="carousel-button-left"
            variant="tertiary"
            size="small"
            className={styles.arrowLeft}
            icon={IconArrowLeft}
            onClick={() => {
              onArrowClick &&
                onArrowClick({
                  direction: 'left',
                  page: currentPage - 1,
                  total: totalPages,
                });
              scroll(card - dimensions.numOfCards);
            }}
            aria-label="Arrow Left"
          />
        )}
        {showLastButton && (
          <Button
            data-qa="carousel-button-right"
            variant="tertiary"
            size="small"
            className={styles.arrowRight}
            icon={IconArrowRight}
            onClick={() => {
              onArrowClick &&
                onArrowClick({
                  direction: 'right',
                  page: currentPage + 1,
                  total: totalPages,
                });
              scroll(card + dimensions.numOfCards);
            }}
            aria-label="Arrow Right"
          />
        )}
      </div>
      <div className={styles.dotsContainer}>
        <PaginationDots
          selected={Math.ceil(card / dimensions.numOfCards)}
          length={Math.ceil(
            React.Children.count(children) / dimensions.numOfCards
          )}
        />
      </div>
    </span>
  );
};

export default withPresets(skins)(Carousel);
